

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './TimingForm.css';

interface TimingFormProps {
  timing: {
    id: number;
    day_of_week: string;
    is_open: boolean;
    open_time: string | null;
    close_time: string | null;
    appointment_type: string;
    slot_duration: number;
  } | null;
  onClose: () => void;
  onSubmit: () => void;
}

const TimingForm: React.FC<TimingFormProps> = ({ timing, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    day_of_week: '',
    open_time: '09:00',
    close_time: '18:00',
    is_open: true,
    special_notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (timing) {
      setFormData({
        day_of_week: timing.day_of_week,
        open_time: timing.open_time ? timing.open_time.substring(0, 5) : '09:00',
        close_time: timing.close_time ? timing.close_time.substring(0, 5) : '18:00',
        is_open: timing.is_open,
        special_notes: ''
      });
    }
  }, [timing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (formData.is_open && (!formData.open_time || !formData.close_time)) {
      setError('Please provide opening and closing times');
      return;
    }

    try {
      setSaving(true);
      
      const response = await api.put(`/admin/working-hours/day/${formData.day_of_week}`, {
        is_open: formData.is_open,
        open_time: formData.is_open ? formData.open_time + ':00' : null,
        close_time: formData.is_open ? formData.close_time + ':00' : null,
        slot_duration: 30
      });

      if (response.data.success) {
        onSubmit();
      } else {
        setError(response.data.message || 'Failed to save timing');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error saving timing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Business Hours - {formData.day_of_week || (timing?.day_of_week || '')}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="is_open"
                  checked={formData.is_open}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Hospital is OPEN on this day</span>
              </label>
            </div>

            {formData.is_open && (
              <div className="time-inputs" style={{ display: 'flex', gap: '20px', marginTop: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="open_time" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Opening Time</label>
                  <input
                    type="time"
                    id="open_time"
                    name="open_time"
                    value={formData.open_time}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="close_time" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Closing Time</label>
                  <input
                    type="time"
                    id="close_time"
                    name="close_time"
                    value={formData.close_time}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>
              </div>
            )}

            <div className="form-group" style={{ marginTop: '15px' }}>
              <label htmlFor="special_notes" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Special Notes</label>
              <textarea
                id="special_notes"
                name="special_notes"
                value={formData.special_notes}
                onChange={handleChange}
                placeholder="e.g., Surgery drop-off: 8 AM - 9 AM"
                rows={3}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px 20px', borderTop: '1px solid #eee', marginTop: '20px' }}>
            <button 
              type="button" 
              className="cancel-btn" 
              onClick={onClose} 
              style={{ 
                padding: '8px 20px', 
                borderRadius: '6px', 
                border: '1px solid #dc3545', 
                background: 'white', 
                color: '#dc3545',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#dc3545';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#dc3545';
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-btn" 
              disabled={saving} 
              style={{ 
                padding: '8px 20px', 
                borderRadius: '6px', 
                border: 'none', 
                background: saving ? '#ccc' : '#667eea', 
                color: 'white', 
                fontSize: '14px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.background = '#5a67d8';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.currentTarget.style.background = '#667eea';
                }
              }}
            >
              {saving ? 'Saving...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimingForm;